import _ from 'underscore';
import { useContext } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { FilterEntity } from '@bingads-webui/filter-entity';
import { get } from '@bingads-webui-universal/primitive-utilities';
import { useIframeDownload } from '@bingads-webui/download-service';
import { useFetch } from '@bingads-webui-campaign/react-hook-use-fetch';
import { odataConfigForMyReport } from './utils';
import { toJsonAndThrowOnError } from './to-json-and-throw-on-error';
import { poll } from './poll';
import taskTypeUtils from './task-type-utils';
import { userErrorCodes } from '../user-error-codes';

const s$ReportEmailRule = window.globalLegacySchema && window.globalLegacySchema.get('ReportEmailRule');

export const reportApiCallsFactory = _.memoize(({
  fetch, scope, serverContext, iframeDownload,
}) => {
  const {
    cid, aid,
  } = scope.values;

  const isInternalUser = serverContext.CurrentUser.IsInternal;

  const urlPrefixModern = aid ?
    `/Customers(${cid})/Accounts(${aid})` :
    `/Customers(${cid})`;

  const urlPrefixLegacy = aid ?
    `/Customers(${serverContext.CurrentUser.ParentCustomerId})/Accounts(${aid})` :
    `/Customers(${serverContext.CurrentUser.ParentCustomerId})`;

  const getRecentReports = toJsonAndThrowOnError(() => {
    const url = `${urlPrefixModern}/TaskItems/Default.GetReportDataTaskItems(pageType=Enum.ReportDataTaskItemPageType'Recent')`;

    const taskItemTypes = [
      aid ? 'ReportUIAccountTemplate' : 'ReportUICustomerTemplate',
      'ReportUITemplate',
      'ReportUIUserTemplate',
    ];
    const urlSearchParams = new URLSearchParams({
      $top: 10,
      $count: true,
      $orderby: 'LastAccessedAt desc',
      customFilter: taskTypeUtils.toODataFilter(taskItemTypes),
    });

    return fetch(`${url}?${urlSearchParams}`);
  });


  const getDefaultReports = toJsonAndThrowOnError(() => {
    const url = `${urlPrefixModern}/TaskItems/Default.GetReportDataTaskItems(pageType=Enum.ReportDataTaskItemPageType'Default')`;

    const taskItemTypes = [
      aid ? 'ReportUIAccountTemplate' : 'ReportUICustomerTemplate',
      'ReportUITemplate',
      'ReportUIUserTemplate',
    ];
    const urlSearchParams = new URLSearchParams({
      $top: 100,
      $count: true,
      $orderby: 'LastAccessedAt desc',
      customFilter: taskTypeUtils.toODataFilter(taskItemTypes),
    });

    return fetch(`${url}?${urlSearchParams}`);
  });


  const getMyReports = toJsonAndThrowOnError(({
    type, top = 20, sort = '', filter = [], skip = 0, includeEmailType = true,
  }) => {
    const taskItemTypes = [
      aid ? 'ReportUIAccountTemplate' : 'ReportUICustomerTemplate',
      'ReportUITemplate',
      'ReportUIUserTemplate',
    ];
    if (includeEmailType) {
      const emailType = aid ? 'ReportAccountEmail' : 'Email';
      taskItemTypes.push(emailType);
    }

    const searchParams = {
      $top: top,
      $skip: skip,
      $count: true,
      customFilter: taskTypeUtils.toODataFilter(taskItemTypes),
    };
    if (filter.length > 0) {
      searchParams.$filter = new FilterEntity({ $and: filter }).odataV4(odataConfigForMyReport);
    }
    if (sort) {
      searchParams.$orderBy = sort;
    }
    const urlSearchParams = new URLSearchParams(searchParams);

    return fetch(`${urlPrefixModern}/TaskItems/Default.GetReportDataTaskItems(pageType=Enum.ReportDataTaskItemPageType'${type}')?${urlSearchParams}`);
  });

  const getSavedReportsForGlobalMenu = () =>
    getMyReports({ type: 'Saved', top: 10, includeEmailType: false });

  function getMyReportsCounts() {
    return Promise.all([
      getMyReports({ type: 'Saved', top: 1 }),
      getMyReports({ type: 'Shared', top: 1 }),
    ]).then(res => res.map(item => item['@odata.count']));
  }

  const getTaskItem = toJsonAndThrowOnError(async (taskItemId) => {
    // For internal user, always use user's parent cid
    // For external user, use different cid for different task item types
    //   ReportUICustomerTemplate / ReportUIAccountTemplate: use current cid
    //   ReportUITemplate: use user's parent cid

    if (isInternalUser) {
      return fetch(`${urlPrefixLegacy}/TaskItems(${taskItemId})`);
    }

    // Try current cid first
    let res = await fetch(`${urlPrefixModern}/TaskItems(${taskItemId})`);

    if (res.status === 400) {
      const body = await res.json();
      if (get(body, 'value[0].Code') === 'InvalidId') {
        // retry with user's parent cid
        res = await fetch(`${urlPrefixLegacy}/TaskItems(${taskItemId})`);
      }
    }

    return res;
  });


  const patchTaskItem = toJsonAndThrowOnError((taskItem, options = { isEmailRule: false }) => {
    let update = taskItem;
    const { isEmailRule } = options;
    if (isEmailRule && s$ReportEmailRule) {
      update = _.extend({
        '@odata.type': '#Model.ReportEmailRule',
      }, _.pick(taskItem, ..._.keys(s$ReportEmailRule.properties)));

      if (!update.Cron) {
        update.Cron = '';
      }

      // when no email needed to be sent, Cron is meaningless, so MT will reject non-empty Cron
      if (update.NotificationDeliveryOption) {
        update.NotificationDeliveryOption['@odata.type'] = '#Model.EmailNotificationDeliveryOption';
      } else {
        update.Cron = '';
      }

      // deprecated property
      delete update.Selection.PeriodComparisonDateRange;
    }

    const urlPrefix = taskTypeUtils.isPrivate(taskItem.Type) ?
      urlPrefixLegacy :
      urlPrefixModern;

    const url = `${urlPrefix}/TaskItems(${taskItem.Id})${isEmailRule ? '' : '?runNow=false'}`;

    return fetch(url, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
  });


  const deleteReportsImpl = toJsonAndThrowOnError((isModern, reports) => {
    const urlPrefix = isModern ? urlPrefixModern : urlPrefixLegacy;

    return fetch(`${urlPrefix}/TaskItems/DeleteReports`, {
      method: 'POST',
      body: JSON.stringify({ ReportIds: _.pluck(reports, 'Id') }),
    });
  });

  async function deleteReports(reports) {
    const {
      modernReports,
      legacyReports,
    } = _.groupBy(reports, ({ Type }) =>
      (taskTypeUtils.isPrivate(Type) ? 'legacyReports' : 'modernReports'));

    await Promise.all([
      modernReports && deleteReportsImpl(true, modernReports),
      legacyReports && deleteReportsImpl(false, legacyReports),
    ]);
  }

  const getReportData = toJsonAndThrowOnError((taskExecutionId, pager = { pageSize: 20, page: 1 }) => {
    const { page, pageSize } = pager;
    const urlSearchParams = new URLSearchParams({
      $top: pageSize,
      $skip: (page - 1) * pageSize,
      $count: true,
      $filter: "TaskItem/Type eq Enum.TaskItemType'ReportUI'",
    });

    return fetch(`${urlPrefixModern}/TaskItemExecutions(${taskExecutionId})/Default.ReportData()?${urlSearchParams}`);
  });


  const submitReportDataTaskItem = toJsonAndThrowOnError((body) => {
    // eslint-disable-next-line no-param-reassign
    delete body.ReportData.GridPagination;

    const prefix = taskTypeUtils.isPrivate(body.ReportData.Type) ?
      urlPrefixLegacy :
      urlPrefixModern;

    const searchParams = new URLSearchParams({
      $expand: 'TaskItemExecutions',
    });

    return fetch(`${prefix}/TaskItems/SubmitReportDataTaskItem?${searchParams}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  });


  const getReportDataTaskItemExecution = toJsonAndThrowOnError((taskExecutionId, urlPrefix = urlPrefixModern) =>
    fetch(`${urlPrefix}/TaskItemExecutions(${taskExecutionId})/Default.PollReportDataTaskItemExecution`));


  async function pollReportDataTaskItemExecution(taskExecutionId, urlPrefix = urlPrefixModern) {
    const result = await poll(
      () => getReportDataTaskItemExecution(taskExecutionId, urlPrefix),
      r => r.Status === 'Completed',
      r => r.Status === 'Failed'
    );

    return result;
  }

  async function submitReportAndWaitForComplete(body) {
    const taskItem = await submitReportDataTaskItem(body);
    const taskExecutionId = taskItem.TaskItemExecutions[0].Id;
    const taskExecutionResult = await pollReportDataTaskItemExecution(taskExecutionId);
    return { taskExecutionId, taskExecutionResult };
  }

  const previewReport = toJsonAndThrowOnError((body) => {
    const previewUrlPrefix = aid ?
      `/Customers(${cid})/Accounts(${aid})` :
      `/Customers(${cid})/Accounts`;
    const url = `${previewUrlPrefix}/Default.PreviewReport`;
    const { ReportData } = body;

    const {
      ReportType,
      Id,
      CampaignReportScopes,
      UserScopes,
      ReportTime,
      ReportTimeZone,
      ReportColumns,
      Filters,
      ReportSorts,
      GridPagination,
      AccountIds,
      CustomerScopes,
      CollapsedRow,
      SchemaName,
    } = ReportData;

    const param = {
      '@odata.type': '#Model.ReportPreviewParameters',
      SchemaName,
      ReportType,
      LCID: serverContext.Omniture.Lcid,
      TaskId: Id,
      AccountIds,
      CampaignReportScopes,
      UserScopes,
      CustomerScopes,
      ReportTime,
      ReportTimeZone,
      ReportColumns,
      Filters,
      ReportSorts,
      GridPagination,
      CollapsedRow,
    };

    return fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        reportPreviewParameters: param,
      }),
    });
  });


  const rerunReport = toJsonAndThrowOnError((report, urlPrefix) =>
    fetch(`${urlPrefix}/TaskItems/RerunReports`, {
      method: 'POST',
      body: JSON.stringify({ ReportIds: [report.Id] }),
    }));


  async function rerunReportAndWaitForComplete(report) {
    const urlPrefix = taskTypeUtils.isPrivate(report.Type) ?
      urlPrefixLegacy :
      urlPrefixModern;

    const rerunResult = await rerunReport(report, urlPrefix);
    const taskItemExecutionId = rerunResult.value[0].TaskItemExecutionId;

    const downloadInfo = await pollReportDataTaskItemExecution(taskItemExecutionId, urlPrefix);
    return downloadInfo;
  }


  async function downloadReport(body) {
    const taskResult = await submitReportAndWaitForComplete(body);
    const downloadUrl = taskResult.taskExecutionResult.ResultId;

    if (downloadUrl) {
      iframeDownload(downloadUrl);
    } else {
      throw new Error('downloadUrl is empty!');
    }
  }

  return {
    getRecentReports,
    getSavedReportsForGlobalMenu,
    getDefaultReports,
    getMyReports,
    getMyReportsCounts,
    getTaskItem,
    getReportDataTaskItemExecution,
    pollReportDataTaskItemExecution,
    getReportData,
    previewReport,
    submitReportDataTaskItem,
    patchTaskItem,
    deleteReports,
    rerunReportAndWaitForComplete,
    downloadReport,
  };
});


// custom hook for function component
export function useReportApiCalls() {
  const { fetch } = useFetch({ userErrorCodes });
  const { scope } = useContext(ScopeContext);
  const serverContext = useContext(ServerContext);
  const iframeDownload = useIframeDownload();

  return reportApiCallsFactory({
    fetch, scope, serverContext, iframeDownload,
  });
}
